<template>
    <TableFieldSet title="Bestätigung">
        <div v-if="loading" class="d-flex justify-content-center p-5">
            <ProgressSpinner />
        </div>
        <div v-else class="col-12 flex flex-column p-2 sm:p-3 sm:pt-0 my-2">
            <template v-if="waitingForPayment">
                <div class="d-flex flex-column align-items-center justify-content-center p-5">
                    <ProgressSpinner />
                    <h3 class="mt-3">Zahlung wird verarbeitet</h3>
                    <p class="text-secondary">Bitte haben Sie einen Moment Geduld...</p>
                </div>
            </template>
            <template v-else-if="paymentFailed">
                <header>
                    <h3 class="mb-3 font-bold text-red-600">Zahlung nicht abgeschlossen</h3>
                </header>
                <Divider/>
                <div class="mt-3">
                    <p>Ihre Zahlung wurde nicht abgeschlossen, daher konnten wir Ihre Reservierung <strong>nicht</strong> bestätigen.</p>
                    <p>Ihre Plätze sind nicht reserviert. Bitte starten Sie die Buchung erneut oder wenden Sie sich an das Restaurant.</p>
                </div>
            </template>
            <template v-else-if="eventData">
                <header>
                    <h3 class="mb-3 font-bold">Vielen Dank für Ihre Bestellung!</h3>
                </header>
                <Divider/>
                <div class="flex-order-0 my-3">
                    <div class="mb-3">
                        <span class="font-bold text-xl">{{ eventData.eventName }}</span>
                    </div>
                    <TableIcon icon="pi pi-calendar">
                        {{ eventData.eventDate }}
                    </TableIcon>
                    <TableIcon icon="pi pi-user">
                        {{ eventData.persons }} Tickets / Personen
                    </TableIcon>
                    <TableIcon icon="pi pi-tag" v-if="eventData.totalCost > 0">
                        {{ formatCurrency(eventData.totalCost, eventData.currency) }}
                    </TableIcon>
                    <TableIcon icon="pi pi-compass">
                        {{ configStore.address }}
                    </TableIcon>
                </div>
                <Divider/>
                <footer>
                    <p class="font-bold text-lg mt-3"> Bestellnummer: {{ displayId || resId }} </p>
                    <div class="mt-3">
                        <p>Die Reservierung war erfolgreich. Sie erhalten Ihre Tickets per E-Mail an die bei der Bestellung angegebene Adresse.</p>
                        <p>Bei Fragen oder Problemen wenden Sie sich bitte direkt an das Restaurant.</p>
                        <p class="font-bold mt-2">Wir wünschen Ihnen viel Spaß bei Ihrem Event und freuen uns auf Ihren Besuch!</p>
                    </div>
                </footer>
            </template>
            <template v-else-if="!isEventFlow">
                <header>
                    <h3 class="mb-3 font-bold"> Reservierung bestätigt! </h3>
                </header>
                <Divider/>
                <div class="flex-order-0 my-3" v-if="tableStore.chosenTable && tableStore.chosenTable.date">
                    <TableIcon icon="pi pi-calendar">
                        {{ tableStore.chosenTable.date }}
                    </TableIcon>
                    <TableIcon icon="pi pi-clock">
                        {{ tableStore.chosenTable.time }}
                    </TableIcon>
                    <TableIcon icon="pi pi-user">
                        {{ tableStore.chosenTable.persons }} Personen
                    </TableIcon>
                    <TableIcon icon="pi pi-compass">
                        {{ configStore.address }}
                    </TableIcon>
                </div>
                <Divider/>
                <footer>
                    <p class="font-bold text-lg mt-3"> Reservierungsnr.: {{ displayId || resId }} </p>
                    <div class="mt-3" v-html="configStore.configObject.texts?.thankyoumessage || 'Vielen Dank für Ihre Reservierung.'"></div>
                </footer>
            </template>
        </div>
    </TableFieldSet>
</template>

<script lang="ts" setup>
import TableFieldSet from "@/components/TableFieldSet.vue";
import TableIcon from "@/components/TableIcon.vue";

import Divider from 'primevue/divider';
import ProgressSpinner from 'primevue/progressspinner';
import {useConfigStore} from "@/stores/ConfigStore";
import {useTableStore} from "@/stores/TableStore";
import {onMounted, onUnmounted, ref} from "vue";
import router from "@/router";
import {useRoute} from "vue-router";
import {useCrud} from "@/composables/CrudAxios";

const configStore = useConfigStore();
const tableStore = useTableStore();
const route = useRoute();
const { getAsync } = useCrud();

const resId = ref('');
const displayId = ref('');
const waitingForPayment = ref(false);
const eventData = ref<any>(null);
const paymentFailed = ref(false);
const loading = ref(true);
// Payments return through /extern/table/payment-return, which marks the event flow.
// Without this marker a failed payment would fall through to the generic
// "Reservierung bestätigt" block, claiming a success that never happened.
const isEventFlow = ref(false);
let pollInterval: any = null;

function formatCurrency(val: number, cur: string) {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: cur || 'EUR' }).format(val);
}

async function checkStatus() {
    if (!resId.value) return;
    try {
        const res: any = await getAsync(`/extern/table/checkreservation?store=${route.query.store}&resid=${encodeURIComponent(resId.value)}`);
        if (!res || !res.success || !res.data) {
            // The reservation could not be verified - never show a confirmation for a payment flow.
            if (isEventFlow.value) {
                waitingForPayment.value = false;
                paymentFailed.value = true;
                if (pollInterval) clearInterval(pollInterval);
            }
            return;
        }
        if (res.data.resid) {
            displayId.value = String(res.data.resid);
        }
        const status = res.data.status;
        // 1 = Awaiting Payment, 2 = Successful
        if (status === 2) {
            waitingForPayment.value = false;
            paymentFailed.value = false;
            eventData.value = res.data;
            if (pollInterval) clearInterval(pollInterval);
        } else if (status === 1) {
            waitingForPayment.value = true;
            isEventFlow.value = true;
            // keep polling
        } else {
            // Cancelled, timed out or refused - this is not a confirmed reservation.
            waitingForPayment.value = false;
            paymentFailed.value = true;
            if (pollInterval) clearInterval(pollInterval);
        }
    } catch (e) {
        console.error(e);
    }
}

onMounted(async () => {
  if(route.query.resid) resId.value = route.query.resid as string;
  isEventFlow.value = route.query.flow === 'event';

  if(!configStore.configObject?.store) {
    if (route.query.store) {
      await configStore.getConfigObject();
    }
  }

  if(!configStore.configObject?.store) {
    router.push({
      name: 'bookingDetails',
      query: {
        store: route.query.store,
      },
    })
    return;
  }
  
  // Initial check
  try {
    await checkStatus();
  } finally {
    loading.value = false;
  }
  
  // If we found an event reservation that is still pending (status 1) or we suspect it might be (e.g. redirected from payment)
  // We can also infer from "waitingForPayment" being true after first check.
  // BUT: if checkStatus returned "success" but status=1, we set waitingForPayment=true.
  // If it's a table reservation, checkStatus might fail or return success=false (if I implemented it strictly for EventReservation).
  // My checkreservationAction implementation returns success=false if EventReservation not found.
  // So if it's a normal table reservation, eventData remains null and waitingForPayment remains false.
  
  if (waitingForPayment.value) {
      pollInterval = setInterval(checkStatus, 3000);
      // Stop waiting at some point instead of spinning forever.
      setTimeout(() => {
          if (pollInterval) clearInterval(pollInterval);
          if (waitingForPayment.value) {
              waitingForPayment.value = false;
              paymentFailed.value = true;
          }
      }, 120000);
  }
})

onUnmounted(() => {
    if (pollInterval) clearInterval(pollInterval);
});

</script>

